/*
 * QQQ - Low-code Application Framework for Engineers.
 * Copyright (C) 2021-2022.  Kingsrook, LLC
 * 651 N Broad St Ste 205 # 6917 | Middletown DE 19709 | United States
 * contact@kingsrook.com
 * https://github.com/Kingsrook/
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import Icon from "@mui/material/Icon";
import Modal from "@mui/material/Modal";
import Tooltip from "@mui/material/Tooltip/Tooltip";
import Typography from "@mui/material/Typography";
import {DataGridPro, GridCallbackDetails, GridColDef, GridDensity, GridEventListener, GridRowParams, GridToolbarContainer, MuiEvent, useGridApiContext, useGridApiEventHandler, useGridApiRef} from "@mui/x-data-grid-pro";
import {Capability} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/Capability";
import {QProcessMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QProcessMetaData";
import {QTableMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QTableMetaData";
import {QWidgetMetaData} from "@temple-of-syrinx/qqq-frontend-core/lib/model/metaData/QWidgetMetaData";
import {QQueryFilter} from "@temple-of-syrinx/qqq-frontend-core/lib/model/query/QQueryFilter";
import {QRecord} from "@temple-of-syrinx/qqq-frontend-core/lib/model/QRecord";
import {makeEditableRow} from "qqq/components/grid/rowEdit/EditableRow";
import {ROW_BANNER_RESERVED_HEIGHT_PX} from "qqq/components/grid/rowEdit/rowEditConstants";
import {EditingRowBannerOverlay} from "qqq/components/grid/rowEdit/EditingRowBannerOverlay";
import {HoverRowActionsOverlay} from "qqq/components/grid/rowEdit/HoverRowActionsOverlay";
import {SavedRowOverlay} from "qqq/components/grid/rowEdit/SavedRowOverlay";
import {RowEditCellRenderer} from "qqq/components/grid/rowEdit/cells/RowEditCellRenderer";
import {RowEditContext, RowEditContextValue} from "qqq/components/grid/rowEdit/RowEditContext";
import {bumpColumnWidthsForEdit, captureGridScroll, preserveFirstVisibleColumn} from "qqq/components/grid/rowEdit/rowEditUtils";
import {useOtherEditorActive} from "qqq/components/grid/rowEdit/globalRowEditRegistry";
import {useRowEditor} from "qqq/components/grid/rowEdit/useRowEditor";
import RecordPickerModal from "qqq/components/misc/RecordPickerModal";
import Widget, {AddNewRecordButton, BulkAddViaFilterButton, LabelComponent, WidgetData} from "qqq/components/widgets/Widget";
import ProcessRun from "qqq/pages/processes/ProcessRun";
import RecordScreenContext from "qqq/pages/records/RecordScreenContext";
import DataGridUtils from "qqq/utils/DataGridUtils";
import HtmlUtils from "qqq/utils/HtmlUtils";
import Client from "qqq/utils/qqq/Client";
import ValueUtils from "qqq/utils/qqq/ValueUtils";
import React, {useCallback, useContext, useEffect, useId, useMemo, useRef, useState} from "react";
import {flushSync} from "react-dom";
import {Link, useNavigate} from "react-router-dom";

export interface ChildRecordListData extends WidgetData
{
   title?: string;
   queryOutput?: { records: { values: any, displayValues?: any } [] };
   childTableMetaData?: QTableMetaData | object;
   tablePath?: string;
   viewAllLink?: string;
   totalRows?: number;
   canAddChildRecord?: boolean;
   defaultValuesForNewChildRecords?: { [fieldName: string]: any };
   disabledFieldsForNewChildRecords?: { [fieldName: string]: any };
   defaultValuesForNewChildRecordsFromParentFields?: { [fieldName: string]: string };
   omitFieldNames?: string[];
   isInProcess?: boolean;
   canBulkAddViaFilter?: boolean;
   bulkAddViaFilterProcessName?: string;
   bulkAddViaFilterPickTableName?: string;
   bulkAddViaFilterPickFieldName?: string;
   bulkAddViaFilterPickerFilter?: string;
}

interface Props
{
   widgetMetaData: QWidgetMetaData,
   data: ChildRecordListData,
   addNewRecordCallback?: () => void,
   disableRowClick: boolean,
   allowRecordEdit: boolean,
   editRecordCallback?: (rowIndex: number) => void,
   allowRecordDelete: boolean,
   deleteRecordCallback?: (rowIndex: number) => void,
   bulkAddChildRecordsCallback?: (rowsValues: { [fieldName: string]: any }[]) => Promise<void>,
   gridOnly?: boolean,
   gridDensity?: GridDensity,
   parentRecord?: QRecord
}

RecordGridWidget.defaultProps =
   {
      disableRowClick: false,
      allowRecordEdit: false,
      allowRecordDelete: false,
      gridOnly: false,
   };

const qController = Client.getInstance();

/*******************************************************************************
 ** build the export-csv content for a set of records & columns.
 *******************************************************************************/
function buildCsvContent(records: QRecord[], columns: any[]): string
{
   let csv = "";
   for (let i = 0; i < columns.length; i++)
   {
      csv += `${i > 0 ? "," : ""}"${ValueUtils.cleanForCsv(columns[i].headerName)}"`;
   }
   csv += "\n";

   for (let i = 0; i < records.length; i++)
   {
      for (let j = 0; j < columns.length; j++)
      {
         const value = records[i].displayValues.get(columns[j].field) ?? records[i].values.get(columns[j].field);
         csv += `${j > 0 ? "," : ""}"${ValueUtils.cleanForCsv(value)}"`;
      }
      csv += "\n";
   }

   return (csv);
}

//////////////////////////////////////////////////////////////////////////////////////
// cap on how many records a picker selection may append as pending association     //
// rows (filter-shaped selections resolve to ids inside the picker, which is asked  //
// for cap+1, letting us detect - and tell the user about - a cut-off selection).   //
//////////////////////////////////////////////////////////////////////////////////////
const BULK_ADD_ASSOCIATION_MAX_ROWS = 1000;

function RecordGridWidget({widgetMetaData, data, addNewRecordCallback, disableRowClick, allowRecordEdit, editRecordCallback, allowRecordDelete, deleteRecordCallback, bulkAddChildRecordsCallback, gridOnly, gridDensity, parentRecord}: Props): JSX.Element
{
   const instance = useRef({timer: null});

   /////////////////////////////////////////////////////////////////////////////////////
   // the live "may the parent record be edited" signal, for callbacks that outlive   //
   // the render they were built in - Widget snapshots the header's label components  //
   // on mount, so a closure over a prop there reads whatever it said at mount        //
   // forever after.                                                                  //
   //                                                                                 //
   // This prop and not data.canAddChildRecord: that one is not a live signal.        //
   // RecordScreenSection writes it onto the widget data object it already handed us, //
   // and only ever writes TRUE, so a ref over that object gives the same answer      //
   // forever.  allowRecordEdit is recomputed from isFormDisabled on every render.    //
   /////////////////////////////////////////////////////////////////////////////////////
   const allowRecordEditRef = useRef<boolean>(allowRecordEdit);
   allowRecordEditRef.current = allowRecordEdit;

   const [rows, setRows] = useState([]);
   const [records, setRecords] = useState([] as QRecord[]);
   const [columns, setColumns] = useState([]);
   const [allColumns, setAllColumns] = useState([]);
   const [csv, setCsv] = useState(null as string);
   const [fileName, setFileName] = useState(null as string);
   const [gridMouseDownX, setGridMouseDownX] = useState(0);
   const [gridMouseDownY, setGridMouseDownY] = useState(0);
   const [childTableMetaData, setChildTableMetaData] = useState(null as QTableMetaData);
   const navigate = useNavigate();
   const gridApiRef = useGridApiRef();

   //////////////////////
   // row-edit state   //
   //////////////////////
   const [editingRowId, setEditingRowId] = useState<string | number | null>(null);
   const [editingRow, setEditingRow] = useState<QRecord | null>(null);
   const formikSubmitRef = useRef<(() => void) | null>(null);

   //////////////////////
   // row-delete state //
   //////////////////////
   const [deleteConfirmRowId, setDeleteConfirmRowId] = useState<string | number | null>(null);
   const [isDeleteSubmitting, setIsDeleteSubmitting] = useState(false);
   const [deleteError, setDeleteError] = useState<string | null>(null);
   const [deletedRowCount, setDeletedRowCount] = useState(0);

   ///////////////////////////////
   // bulk-add-via-filter state //
   ///////////////////////////////
   const [showRecordPicker, setShowRecordPicker] = useState(false);
   const [bulkAddSelection, setBulkAddSelection] = useState(null as string[] | QQueryFilter);
   const [bulkAddProcess, setBulkAddProcess] = useState(null as QProcessMetaData);
   const [bulkAddPickTableMetaData, setBulkAddPickTableMetaData] = useState(null as QTableMetaData);

   //////////////////////////////////////////////////////////////////////////////////////////
   // how many of the picker's criteria came from the seed filter rather than from the     //
   // user.  RecordQuery renders that many leading criteria read-only and re-asserts them  //
   // on every filter change, so without this count the seed is an ordinary criteria row:  //
   // removable by its X, by the quick-filter x, by Reset All Changes or by New View, and  //
   // the picker would then offer records the widget's filter was there to exclude.        //
   //                                                                                      //
   // the filter is scoping, not decoration, which is why it is locked here the same way   //
   // DynamicSelect locks its own source-table filter.                                     //
   //                                                                                      //
   // parsed only for the count - the modal parses the JSON itself, because it also has to //
   // resolve possible-value labels against the table's metadata before showing it.  A     //
   // document that will not parse yields no locked criteria, and the modal reports it.    //
   //////////////////////////////////////////////////////////////////////////////////////////
   const bulkAddPickerLockedCriteriaCount = useMemo(() =>
   {
      if (!data?.bulkAddViaFilterPickerFilter)
      {
         return (0);
      }

      try
      {
         return (JSON.parse(data.bulkAddViaFilterPickerFilter)?.criteria?.length ?? 0);
      }
      catch (e)
      {
         return (0);
      }
   }, [data?.bulkAddViaFilterPickerFilter]);

   ///////////////////////////////////////////////////////////////////////////////////
   // Read parent record-screen mode so we can gate row editing on view mode only.  //
   ///////////////////////////////////////////////////////////////////////////////////
   const recordScreenCtx = useContext(RecordScreenContext);
   const parentMode = recordScreenCtx?.mode;

   const canEdit = childTableMetaData != null
      && childTableMetaData.capabilities.has(Capability.TABLE_UPDATE)
      && childTableMetaData.editPermission;

   ///////////////////////////////////////////////////////////////////////////////////
   // Stable per-instance id for the cross-widget "only one row edited at a time"    //
   // registry.  useId gives a React-stable value across renders and distinct per    //
   // mounted widget — so each RecordGridWidget claims/releases with its own key.   //
   ///////////////////////////////////////////////////////////////////////////////////
   const rowEditHostId = useId();
   const anotherEditorActive = useOtherEditorActive(rowEditHostId);

   ///////////////////////////////////////////////////////////////////////////////////
   // When another widget already has an active row edit, this widget's rows become  //
   // effectively non-editable (only one row can be edited at a time page-wide).     //
   // Folding this into isRowEditable is what routes EditRowActions through the      //
   // disabled-pencil branch it already supports — combined with the                  //
   // rowEditDisabledMessage below, the user gets "pencil visible on hover but       //
   // disabled, with this tooltip" instead of a normal clickable pencil.             //
   ///////////////////////////////////////////////////////////////////////////////////
   const isRowEditable = canEdit && parentMode === "view" && !anotherEditorActive;

   ///////////////////////////////////////////////////////////////////////////////////
   // Two reasons the pencil can show up disabled-on-hover:                          //
   //   1. parent RecordScreen is itself in edit/create mode                         //
   //   2. another widget on the page already has an active row edit                 //
   // If both apply, the parent-mode message wins since it's the more fundamental    //
   // constraint — once they cancel the parent edit, they're back at case (2).       //
   ///////////////////////////////////////////////////////////////////////////////////
   const canDelete = childTableMetaData != null
      && childTableMetaData.capabilities.has(Capability.TABLE_DELETE)
      && childTableMetaData.deletePermission;
   const isRowDeletable = canDelete && parentMode === "view" && !anotherEditorActive;

   ///////////////////////////////////////////////////////////////////////////////////
   // Association mode: the manage-association contexts (parent edit/create screen, //
   // in-process) pass allowRecordEdit/Delete + callbacks.  Their actions render on //
   // the same hover overlay, but route to the association callbacks (modal edit    //
   // pending-list removal) instead of inline-edit / real delete.                   //
   //                                                                               //
   // EXCEPT on a record screen whose parent is being edited or created: child      //
   // grids there are READ-ONLY (product decision 2026-07-13) — hover shows         //
   // disabled pencil/trash with explainer tooltips instead.  (Add-new is a         //
   // separate widget-label button and is unaffected.)                              //
   ///////////////////////////////////////////////////////////////////////////////////
   const parentBeingEdited = recordScreenCtx != null && parentMode !== "view";
   const associationMode = Boolean(allowRecordEdit || allowRecordDelete) && !parentBeingEdited;

   const rowEditDisabledMessage = ((canEdit || allowRecordEdit) && parentMode !== "view")
      ? "Sub-records cannot be edited while their parent record is being edited"
      : (canEdit && anotherEditorActive)
         ? "Only one row can be edited at a time"
         : undefined;

   const rowDeleteDisabledMessage = ((canDelete || allowRecordDelete) && parentMode !== "view")
      ? "Sub-records cannot be deleted while their parent record is being edited"
      : (canDelete && anotherEditorActive)
         ? "A row cannot be deleted while another row is being edited"
         : undefined;


   //////////////////////////////////////////////////////////////////////////////
   // row-edit hook + derived context value + EditableRow component (memoised) //
   //////////////////////////////////////////////////////////////////////////////
   const rowEditor = useRowEditor({
      tableMetaData: childTableMetaData,
      editingRow,
      editingRowId,
      hostId: rowEditHostId,
      onFormDisabled: recordScreenCtx?.onChildFormDisabled,
      onSaveComplete: (saved) =>
      {
         const refreshedRows = DataGridUtils.makeRows([saved], childTableMetaData, undefined, true);
         const refreshedRow = refreshedRows.length > 0 ? refreshedRows[0] : null;
         ////////////////////////////////////////////////////////////////////////////////
         // flushSync commits the refreshed row data BEFORE we clear editingRowId, so  //
         // display cells read the new values on the first read-only paint.  ALSO      //
         // update the raw `records` QRecord list — rowEditOnEnterEdit reads from that //
         // on re-entry, so a stale record there causes the second edit to seed Formik //
         // with pre-save values.                                                      //
         ////////////////////////////////////////////////////////////////////////////////
         const rowIdToUpdate = editingRowId;
         if (refreshedRow)
         {
            flushSync(() =>
            {
               setRows((prev: any[]) => prev.map((r: any) =>
               {
                  if (r.__rowIndex === rowIdToUpdate)
                  {
                     return {...refreshedRow, __rowIndex: r.__rowIndex};
                  }
                  return r;
               }));
            });
         }
         setRecords((prev: QRecord[]) =>
         {
            if (!prev) return prev;
            const copy = [...prev];
            const idx = typeof rowIdToUpdate === "number" ? rowIdToUpdate : Number(rowIdToUpdate);
            if (!Number.isNaN(idx) && idx >= 0 && idx < copy.length)
            {
               copy[idx] = saved;
            }
            return copy;
         });
         const restoreScroll = captureGridScroll(gridApiRef);
         setEditingRowId(null);
         setEditingRow(null);
         //////////////////////////////////////////////////////////////////////////////
         // DataGrid caches the auto-measured height of the editing row; without a   //
         // reset, successive edits of the same row stack the cached heights and the //
         // row grows taller every time.                                             //
         //////////////////////////////////////////////////////////////////////////////
         gridApiRef.current?.resetRowHeights?.();
         restoreScroll();
      },
      onCancel: () =>
      {
         const restoreScroll = captureGridScroll(gridApiRef);
         setEditingRowId(null);
         setEditingRow(null);
         gridApiRef.current?.resetRowHeights?.();
         restoreScroll();
      },
   });

   const rowEditContextValue: RowEditContextValue = {
      editingRowId,
      savedRowId: rowEditor.savedRowId,
      formFieldDefs: rowEditor.formFieldDefs,
      isSaving: rowEditor.isSaving,
      rowBannerState: rowEditor.rowBannerState,
      handleFieldBlur: rowEditor.handleFieldBlur,
      requestSave: () => formikSubmitRef.current?.(),
      requestCancel: () =>
      {
         const restoreScroll = captureGridScroll(gridApiRef);
         setEditingRowId(null);
         setEditingRow(null);
         gridApiRef.current?.resetRowHeights?.();
         restoreScroll();
      },
   };

   const rowEditOnEnterEdit = useCallback((rowId: string | number) =>
   {
      /////////////////////////////////////////////////////////////////////////////////
      // Use the QRecord from records (raw values) rather than the flat grid-row     //
      // object, which contains DISPLAY values from DataGridUtils.makeRows.  Initial //
      // form values MUST be raw values so the save payload round-trips correctly.   //
      /////////////////////////////////////////////////////////////////////////////////
      const index = typeof rowId === "number" ? rowId : Number(rowId);
      const rec = (records && !isNaN(index)) ? records[index] : null;
      if (!rec) return;
      /////////////////////////////////////////////////////////////////////////////////
      // Clear any stale page-level formDisabled alert from a prior edit attempt so  //
      // the user doesn't see a leftover "record cannot be edited" message when they //
      // re-enter edit on a row that now IS editable.                                //
      /////////////////////////////////////////////////////////////////////////////////
      recordScreenCtx?.onChildRowEditStart?.();
      setEditingRowId(rowId);
      setEditingRow(rec);
   }, [records, recordScreenCtx]);

   const EditableRowComponent = useMemo(() => makeEditableRow({
      initialValues: rowEditor.initialValues,
      validationSchema: rowEditor.validationSchema,
      onSubmit: rowEditor.handleSubmit,
      onFieldChange: rowEditor.onFieldChange,
      formValuesRef: rowEditor.formValuesRef,
      formValuesJSONRef: rowEditor.formValuesJSONRef,
      setFieldValueRef: rowEditor.setFieldValueRef,
      formikSubmitRef,
      setRowBannerState: rowEditor.setRowBannerState,
   }), [rowEditor.initialValues, rowEditor.validationSchema, rowEditor.setRowBannerState]);

   ///////////////////////////////////////////////////////////////////////////////////////////////
   // bump column widths to editable minimums once formFieldDefs are populated for the edit row //
   ///////////////////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      if (editingRowId != null && Object.keys(rowEditor.formFieldDefs).length > 0)
      {
         //////////////////////////////////////////////////////////////////////////////////
         // flushSync commits new widths synchronously so the scroll correction lands in //
         // the same browser paint — avoids the shifted-then-restored flicker.           //
         //////////////////////////////////////////////////////////////////////////////////
         const restoreHScroll = preserveFirstVisibleColumn(gridApiRef);
         flushSync(() =>
         {
            bumpColumnWidthsForEdit(gridApiRef, rowEditor.formFieldDefs);
         });
         restoreHScroll();
      }
   }, [editingRowId, rowEditor.formFieldDefs, gridApiRef]);

   //////////////////////////////////////////////////////////////////////////////////
   // When the row banner transitions into or out of a non-"none" kind, force the  //
   // grid to re-measure row heights.  Without this, a "saveError" banner appearing //
   // mid-edit is absolutely-positioned on top of the next row because DataGrid     //
   // has cached the pre-banner (shorter) auto-height for the editing row.         //
   //                                                                              //
   // The banner text renders in a portal and EditableRow's inner spacer resizes    //
   // via its ResizeObserver a frame or two AFTER the bannerState flip.  We thus    //
   // trigger TWO resets: one immediately (catches the pure getRowHeight return     //
   // change) and one on the next rAF (catches the spacer growth once the portal   //
   // content has painted).                                                         //
   //////////////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      if (editingRowId == null) return;
      gridApiRef.current?.resetRowHeights?.();
      const frame1 = requestAnimationFrame(() =>
      {
         gridApiRef.current?.resetRowHeights?.();
         requestAnimationFrame(() =>
         {
            gridApiRef.current?.resetRowHeights?.();
         });
      });
      return () => cancelAnimationFrame(frame1);
   }, [rowEditor.rowBannerState.kind, editingRowId, gridApiRef]);

   /////////////////////////////////////////////////////////////////////////
   // Force-cancel child row edit when parent transitions out of view mode //
   /////////////////////////////////////////////////////////////////////////
   useEffect(() =>
   {
      if (parentMode !== "view" && editingRowId != null)
      {
         setEditingRowId(null);
         setEditingRow(null);
      }
   }, [parentMode, editingRowId]);


   /***************************************************************************
    * There's a table meta data object in the widget data - but - the type
    * has evolved over time.  originally, it could have been a QTableMetaData
    * else it could have been object we'd need to pass to the QTableMetaData
    * constructor.
    *
    * Now, we're migrating to have a QFrontendTableMetaData available from the
    * backend (to include full join table objects, as frontend expects) - which,
    * actually will come here as an object off of the widget call.
    *
    * so, all of this method is to just deal with whatever data we have, and
    * return the QTableMetaData object to use.
    *
    * (note, we do set this as a state var (childTableMetaData), but there was
    * an issue w/ the initial render and buttons getting "baked", where that
    * state var wasn't set yet (it's from a useEffect), so we extracted this
    * into a function that can be called for that button-builder
    ***************************************************************************/
   function getTableMetaDataFromWidgetData(data: ChildRecordListData): QTableMetaData
   {
      return data.childFrontendTableMetaData ?
         (data.childFrontendTableMetaData instanceof QTableMetaData ? data.childFrontendTableMetaData as QTableMetaData : new QTableMetaData(data.childFrontendTableMetaData)) :
         (data.childTableMetaData instanceof QTableMetaData ? data.childTableMetaData as QTableMetaData : new QTableMetaData(data.childTableMetaData));
   }


   useEffect(() =>
   {
      (async () =>
      {
         ///////////////////////////////////////////////////////////////////////////////////////////////
         // october 2025, we are migrating from childTableMetaData, which from the backend was not a  //
         // QFrontendTableMetaData - to childFrontendTableMetaData, which is a QFrontendTableMetaData //
         // and as such, contains join tables as actual tables, not just table names.                 //
         ///////////////////////////////////////////////////////////////////////////////////////////////
         if (data && (data.childTableMetaData || data.childFrontendTableMetaData) && data.queryOutput)
         {
            const records: QRecord[] = [];
            const queryOutputRecords = data.queryOutput.records;
            if (queryOutputRecords)
            {
               for (let i = 0; i < queryOutputRecords.length; i++)
               {
                  if (queryOutputRecords[i] instanceof QRecord)
                  {
                     records.push(queryOutputRecords[i] as QRecord);
                  }
                  else
                  {
                     records.push(new QRecord(queryOutputRecords[i]));
                  }
               }
            }

            const tableMetaData = getTableMetaDataFromWidgetData(data);
            setChildTableMetaData(tableMetaData);

            const rows = DataGridUtils.makeRows(records, tableMetaData, undefined, true);

            /////////////////////////////////////////////////////////////////////////////////
            // note - tablePath may be null, if the user doesn't have access to the table. //
            /////////////////////////////////////////////////////////////////////////////////
            const childTablePath = data.tablePath ? data.tablePath + (data.tablePath.endsWith("/") ? "" : "/") : data.tablePath;
            const metaData = await qController.loadMetaData();
            const includeExposedJoinTables: string[] = data.includeExposedJoinTables ?? []
            const columns = DataGridUtils.setupGridColumns(tableMetaData, childTablePath, metaData, "bySection", includeExposedJoinTables);

            if (data.omitFieldNames)
            {
               for (let i = 0; i < columns.length; i++)
               {
                  const column = columns[i];
                  if (data.omitFieldNames.indexOf(column.field) > -1)
                  {
                     columns.splice(i, 1);
                     i--;
                  }
               }
            }

            if(data.onlyIncludeFieldNames)
            {
               for (let i = 0; i < columns.length; i++)
               {
                  const column = columns[i];
                  if (data.onlyIncludeFieldNames.indexOf(column.field) == -1)
                  {
                     columns.splice(i, 1);
                     i--;
                  }
               }
            }

            /////////////////////////////////////////////////////////////////////////////////////////////////////////////
            // capture all-columns to use for the export (before we might splice some away from the on-screen display) //
            /////////////////////////////////////////////////////////////////////////////////////////////////////////////
            const allColumns = [...columns];
            setAllColumns(JSON.parse(JSON.stringify(columns)));

            ////////////////////////////////////////////////////////////////
            // do not not show the foreign-key column of the parent table //
            ////////////////////////////////////////////////////////////////
            if (data.defaultValuesForNewChildRecords)
            {
               for (let i = 0; i < columns.length; i++)
               {
                  if (data.defaultValuesForNewChildRecords[columns[i].field])
                  {
                     columns.splice(i, 1);
                     i--;
                  }
               }
            }

            /////////////////////////////////////////////////////////////////////////////////////
            // wrap every column's renderCell with RowEditCellRenderer so that the editing row //
            // shows edit inputs while all other rows continue to show display values.         //
            /////////////////////////////////////////////////////////////////////////////////////
            for (const col of columns)
            {
               const originalRender = col.renderCell ?? ((params: any) => params.formattedValue ?? params.value);
               col.renderCell = (params: any) => <RowEditCellRenderer params={params} displayRenderer={originalRender} />;
            }

            ///////////////////////////////////////////////////////////////////////////////////
            // Row-edit pencil/check/cancel is rendered as a floating overlay by EditableRow //
            // (components.Row slot) — no dedicated column is added here.                    //
            ///////////////////////////////////////////////////////////////////////////////////

            setRows(rows);
            setRecords(records);
            setColumns(columns);

            ///////////////////////////////////////////////////////////////////////////////
            // fresh query output means any locally-deleted rows are already reflected   //
            // in data.totalRows — reset the local deletion counter so the "of N" footer //
            // doesn't double-subtract them                                              //
            ///////////////////////////////////////////////////////////////////////////////
            setDeletedRowCount(0);

            const fileName = (data?.label ?? widgetMetaData.label) + " " + ValueUtils.formatDateTimeForFileName(new Date()) + ".csv";

            setCsv(buildCsvContent(records, allColumns));
            setFileName(fileName);
         }
      })();
   }, [JSON.stringify(data?.queryOutput)]);

   ///////////////////
   // view all link //
   ///////////////////
   const labelAdditionalElementsLeft: JSX.Element[] = [];
   if (data && data.viewAllLink)
   {
      labelAdditionalElementsLeft.push(
         <Typography key={"viewAllLink"} variant="body2" p={2} display="inline" fontSize=".875rem" pt="0" position="relative">
            <Link to={data.viewAllLink}>View All</Link>
         </Typography>
      );
   }

   ///////////////////
   // export button //
   ///////////////////
   let isExportDisabled = true;
   let tooltipTitle = "Export";
   if (data && childTableMetaData && data.queryOutput && data.queryOutput.records && data.queryOutput.records.length > 0)
   {
      isExportDisabled = false;

      if (data.totalRows && data.queryOutput.records.length < data.totalRows)
      {
         tooltipTitle = "Export these " + data.queryOutput.records.length + " records.";
         if (data.viewAllLink)
         {
            tooltipTitle += "\nClick View All to export all records.";
         }
      }
   }

   const onExportClick = () =>
   {
      if (csv)
      {
         HtmlUtils.download(fileName, csv);
      }
      else
      {
         alert("There is no data available to export.");
      }
   };

   if (widgetMetaData?.showExportButton)
   {
      labelAdditionalElementsLeft.push(
         <Typography key={"exportButton"} variant="body2" px={0} display="inline" position="relative">
            <Tooltip title={tooltipTitle}><span><Button sx={{px: 1, py: 0, minWidth: "initial"}} onClick={onExportClick} disabled={isExportDisabled}><Icon sx={{color: "#757575", fontSize: 1.25}}>save_alt</Icon></Button></span></Tooltip>
         </Typography>
      );
   }

   ////////////////////
   // add new button //
   ////////////////////
   const labelAdditionalComponentsRight: LabelComponent[] = [];
   if (data && data.canAddChildRecord)
   {
      let disabledFields = data.disabledFieldsForNewChildRecords;
      if (!disabledFields)
      {
         disabledFields = data.defaultValuesForNewChildRecords;
      }

      const defaultValuesForNewChildRecords = data.defaultValuesForNewChildRecords || {};

      ///////////////////////////////////////////////////////////////////////////////////////
      // copy values from specified fields in the parent record down into the child record //
      ///////////////////////////////////////////////////////////////////////////////////////
      if (data.defaultValuesForNewChildRecordsFromParentFields)
      {
         for (let childField in data.defaultValuesForNewChildRecordsFromParentFields)
         {
            const parentField = data.defaultValuesForNewChildRecordsFromParentFields[childField];
            defaultValuesForNewChildRecords[childField] = parentRecord?.values?.get(parentField);
         }
      }

      labelAdditionalComponentsRight.push(new AddNewRecordButton(getTableMetaDataFromWidgetData(data), defaultValuesForNewChildRecords, "Add new", disabledFields, addNewRecordCallback));
   }

   /////////////////////////////////////////////////////////////////////////////////////////
   // bulk-add-via-filter button - only when the backend advertises it, and only in plain //
   // view mode (not the in-process/association contexts, which pass a callback and/or    //
   // set isInProcess).  the parent record id comes from the record-screen context - the  //
   // same parent this widget's rows were queried under - and is required, since the      //
   // process needs it as a process value.                                                //
   /////////////////////////////////////////////////////////////////////////////////////////
   const bulkAddParentRecordId = recordScreenCtx?.record?.values?.get(recordScreenCtx?.tableMetaData?.primaryKeyField);
   if (data && data.canBulkAddViaFilter && data.bulkAddViaFilterProcessName && data.bulkAddViaFilterPickTableName && !addNewRecordCallback && !data.isInProcess && parentMode === "view" && bulkAddParentRecordId != null)
   {
      labelAdditionalComponentsRight.push(new BulkAddViaFilterButton(() => setShowRecordPicker(true)));
   }

   ////////////////////////////////////////////////////////////////////////////////////////
   // association mode (create/edit screens, where addNewRecordCallback manages pending  //
   // rows in-memory): intersection-mode widgets (pickFieldName advertised) get Bulk Add //
   // too - but picked records become pending association rows via the bulk-append       //
   // callback (no process run; they save WITH the parent).  re-parent widgets advertise //
   // no pickFieldName, so they stay view-screen-only.  canAddChildRecord is the same    //
   // signal that gates the Add-new button (cleared when the parent form is disabled).   //
   ////////////////////////////////////////////////////////////////////////////////////////
   const bulkAddAssociationMode = Boolean(data && data.canBulkAddViaFilter && data.canAddChildRecord && data.bulkAddViaFilterPickTableName && data.bulkAddViaFilterPickFieldName && addNewRecordCallback && !data.isInProcess && bulkAddChildRecordsCallback);
   if (bulkAddAssociationMode)
   {
      ////////////////////////////////////////////////////////////////////////////////////
      // re-checked at CLICK time, off a ref, and not only in the gate above.  Widget   //
      // snapshots labelAdditionalComponentsRight into state on mount only, so the gate //
      // is evaluated once, and an on-load adjuster reporting isFormDisabled after      //
      // mount would otherwise leave a live Bulk Add button on a form the user cannot   //
      // save, appending rows to a pending list going nowhere.                          //
      //                                                                                //
      // Off allowRecordEdit rather than canAddChildRecord - see allowRecordEditRef.    //
      ////////////////////////////////////////////////////////////////////////////////////
      labelAdditionalComponentsRight.push(new BulkAddViaFilterButton(() =>
      {
         if (!allowRecordEditRef.current)
         {
            return;
         }
         setShowRecordPicker(true);
      }));
   }


   /*******************************************************************************
    ** callback for the bulk-add record picker - stash the selection, load the
    ** process's meta-data, and open the process modal.
    *******************************************************************************/
   async function runBulkAddProcessForPickedRecords(selection: string[] | QQueryFilter)
   {
      setShowRecordPicker(false);
      setBulkAddSelection(selection);

      try
      {
         const processMetaData = await qController.loadProcessMetaData(data.bulkAddViaFilterProcessName);
         setBulkAddProcess(processMetaData);
      }
      catch (e: any)
      {
         /////////////////////////////////////////////////////////////////////////////////////
         // the picker is already closed at this point, so tell the user their selection    //
         // went nowhere (same plain-alert pattern as this widget's export button), and     //
         // clear the stashed selection so a later success doesn't reuse a stale selection. //
         /////////////////////////////////////////////////////////////////////////////////////
         console.error("Error loading bulk-add process meta-data", e);
         setBulkAddSelection(null);
         alert("Error loading the Bulk Add process: " + (e?.message ?? "Details not available.") + "\nYour selection was not applied - please try again.");
      }
   }


   /*******************************************************************************
    ** association-mode callback for the bulk-add record picker (create/edit
    ** screens) - the picker hands us record ids (filter-shaped selections were
    ** resolved to ids inside RecordQuery, join & variant aware); skip ids already
    ** in the grid, and append the rest as pending association rows.
    *******************************************************************************/
   async function addPickedRecordsAsAssociations(recordIds: string[])
   {
      setShowRecordPicker(false);

      try
      {
         const pickFieldName = data.bulkAddViaFilterPickFieldName;

         ///////////////////////////////////////////////////////////////////////////////
         // the picker was asked for cap+1 ids - so getting more than the cap means   //
         // the selection was genuinely cut off (and we should say so); trim to cap.  //
         ///////////////////////////////////////////////////////////////////////////////
         let pickedIds: string[] = recordIds;
         let cappedAt: number = null;
         if (pickedIds.length > BULK_ADD_ASSOCIATION_MAX_ROWS)
         {
            pickedIds = pickedIds.slice(0, BULK_ADD_ASSOCIATION_MAX_ROWS);
            cappedAt = BULK_ADD_ASSOCIATION_MAX_ROWS;
         }

         //////////////////////////////////////////////////////////////////////////
         // skip ids already present in the grid's rows (pending + loaded) - the //
         // records state mirrors data.queryOutput.records.                      //
         //////////////////////////////////////////////////////////////////////////
         const existingIds = new Set<string>();
         for (let i = 0; i < records.length; i++)
         {
            const value = records[i]?.values?.get(pickFieldName);
            if (value != null)
            {
               existingIds.add(String(value));
            }
         }

         ////////////////////////////////////////////////////////////////////////////////////
         // de-duplicated against itself as well as against the grid: resolving a          //
         // filter-shaped selection over a joined table can return one record's id several //
         // times, which would append duplicate pending rows.  Counting off the original   //
         // list means skippedCount reports the repeats too.                               //
         ////////////////////////////////////////////////////////////////////////////////////
         const remainingIds = [...new Set(pickedIds.map(String))].filter(id => !existingIds.has(id));
         const skippedCount = pickedIds.length - remainingIds.length;

         if (remainingIds.length > 0)
         {
            await bulkAddChildRecordsCallback(remainingIds.map(id => ({[pickFieldName]: id})));
         }

         const messages: string[] = [];
         if (cappedAt != null)
         {
            messages.push(`Up to the first ${cappedAt.toLocaleString()} matching records were considered.`);
         }
         if (skippedCount > 0)
         {
            messages.push(skippedCount == 1 ? "1 record was already in the list and was skipped." : `${skippedCount.toLocaleString()} records were already in the list and were skipped.`);
         }
         if (messages.length > 0)
         {
            ////////////////////////////////////////////////////////////////////////////////
            // these outcomes are informational - the records WERE added - so they belong //
            // in the page's warning alert, not a modal the user has to dismiss.  fall    //
            // back to alert() only where there is no record screen around us.            //
            ////////////////////////////////////////////////////////////////////////////////
            const message = messages.join("  ");
            if (recordScreenCtx?.onChildWarning)
            {
               recordScreenCtx.onChildWarning(message);
            }
            else
            {
               alert(message);
            }
         }
      }
      catch (e: any)
      {
         console.error("Error bulk-adding association records", e);
         alert("Error adding the selected records: " + (e?.message ?? "Details not available.") + "\nYour selection was not applied - please try again.");
      }
   }


   /*******************************************************************************
    ** close callback for the bulk-add process modal - and refresh the widget, so
    ** the newly attached records show up.
    *******************************************************************************/
   function closeBulkAddProcess(event: object, reason: string)
   {
      if (reason == "backdropClick" || reason == "escapeKeyDown")
      {
         return;
      }

      setBulkAddProcess(null);
      setBulkAddSelection(null);
      recordScreenCtx?.reloadWidget?.(widgetMetaData.name);
   }


   /////////////////////////////////////////////////////////////////
   // if a grid preference window is open, ignore and reset timer //
   /////////////////////////////////////////////////////////////////
   /*******************************************************************************
    ** view-mode row delete: close the confirm dialog, clearing any error.
    *******************************************************************************/
   function closeDeleteDialog()
   {
      setDeleteConfirmRowId(null);
      setDeleteError(null);
   }


   /*******************************************************************************
    ** view-mode row delete: called from the confirm dialog's Yes button — really
    ** delete the child record, then remove its row from the grid in place.
    *******************************************************************************/
   async function handleDeleteConfirm()
   {
      if (deleteConfirmRowId == null || !childTableMetaData)
      {
         return;
      }
      const idx = typeof deleteConfirmRowId === "number" ? deleteConfirmRowId : Number(deleteConfirmRowId);
      const record = records[idx];
      const primaryKey = record?.values?.get(childTableMetaData.primaryKeyField);
      if (primaryKey == null)
      {
         setDeleteError("Could not determine the record to delete.");
         return;
      }

      setIsDeleteSubmitting(true);
      setDeleteError(null);
      try
      {
         await qController.delete(childTableMetaData.name, primaryKey);

         //////////////////////////////////////////////////////////////////////////////
         // remove the row; leave `records` positions intact (rows key records by    //
         // __rowIndex, so remaining rows keep their original index into `records`). //
         //////////////////////////////////////////////////////////////////////////////
         const survivingRows = rows.filter((r: any) => r.__rowIndex !== deleteConfirmRowId);
         setRows(survivingRows);
         setDeletedRowCount((prev) => prev + 1);

         ///////////////////////////////////////////////////////////////////////////
         // rebuild the export csv from the surviving rows, so an export after an //
         // in-view delete matches what's on screen                               //
         ///////////////////////////////////////////////////////////////////////////
         setCsv(buildCsvContent(survivingRows.map((r: any) => records[r.__rowIndex]).filter((r: QRecord) => r != null), allColumns));

         setDeleteConfirmRowId(null);
      }
      catch (e: any)
      {
         setDeleteError(e?.message ?? "Error deleting record.");
      }
      finally
      {
         setIsDeleteSubmitting(false);
      }
   }


   const handleRowClick = (params: GridRowParams, event: MuiEvent<React.MouseEvent>, details: GridCallbackDetails) =>
   {
      if (disableRowClick)
      {
         return;
      }

      ////////////////////////////////////////////////////////////////////////////////////
      // Suppress row-click navigation when a row is being edited inline.  Clicks that  //
      // originate inside the editing row (e.g., toggling a BooleanFieldSwitch) can     //
      // bubble to the DataGrid row and would otherwise navigate the user away from the //
      // parent view page, losing the in-progress edit.                                 //
      ////////////////////////////////////////////////////////////////////////////////////
      if (editingRowId != null)
      {
         return;
      }

      (async () =>
      {
         const qInstance = await qController.loadMetaData();
         let tablePath = qInstance.getTablePathByName(childTableMetaData?.name);
         if (tablePath)
         {
            tablePath = `${tablePath}/${params.row[childTableMetaData.primaryKeyField]}`;
            DataGridUtils.handleRowClick(tablePath, event, gridMouseDownX, gridMouseDownY, navigate, instance);
         }
      })();
   };

   /*******************************************************************************
    ** So that we can useGridApiContext to add event handlers for mouse down and
    ** row double-click (to make it so you don't accidentally click into records),
    ** we have to define a grid component, so even though we don't want a custom
    ** toolbar, that's why we have this (and why it returns empty)
    *******************************************************************************/
   function CustomToolbar()
   {
      const handleMouseDown: GridEventListener<"cellMouseDown"> = (params, event, details) =>
      {
         setGridMouseDownX(event.clientX);
         setGridMouseDownY(event.clientY);
         clearTimeout(instance.current.timer);
      };

      const handleDoubleClick: GridEventListener<"rowDoubleClick"> = (event: any) =>
      {
         clearTimeout(instance.current.timer);
      };

      const apiRef = useGridApiContext();
      useGridApiEventHandler(apiRef, "cellMouseDown", handleMouseDown);
      useGridApiEventHandler(apiRef, "rowDoubleClick", handleDoubleClick);

      return (<GridToolbarContainer />);
   }

   let containerPadding = -3;
   if (data?.isInProcess)
   {
      containerPadding = 0;
   }


   //////////////////////////////////////////////////////////////////////////////////
   // For the common case we let DataGridPro's `autoHeight` size the grid — it     //
   // knows the real header/row/footer/scrollbar heights including borders, which  //
   // an arithmetic estimate gets wrong by a few pixels and triggers a spurious   //
   // vertical scrollbar.  When the row-edit banner is active we override with a   //
   // manual height because autoHeight does not recompute when per-row heights    //
   // change dynamically (banner adds vertical space to the editing row).         //
   //////////////////////////////////////////////////////////////////////////////////
   const densityRowHeight = (gridDensity ?? "standard") === "compact" ? 35 : (gridDensity === "comfortable" ? 64 : 52);
   const bannerKind = rowEditor.rowBannerState.kind;
   const bannerActive = editingRowId != null && bannerKind !== "none" && bannerKind !== "saveSuccess";
   const GRID_HEADER_HEIGHT_PX = 56;
   const GRID_FOOTER_HEIGHT_PX = 52;
   const MIN_ROWS_AREA_HEIGHT = 60;
   const useAutoHeight = !bannerActive;
   const computedGridHeight = useAutoHeight ? undefined :
      GRID_HEADER_HEIGHT_PX
      + Math.max(MIN_ROWS_AREA_HEIGHT, (rows?.length ?? 0) * densityRowHeight)
      + ROW_BANNER_RESERVED_HEIGHT_PX + 8
      + GRID_FOOTER_HEIGHT_PX;

   const grid = (
      <RowEditContext.Provider value={rowEditContextValue}>
         <DataGridPro
            apiRef={gridApiRef}
            autoHeight={useAutoHeight}
            sx={{
               borderBottom: "none",
               borderLeft: "none",
               borderRight: "none",
               ...(useAutoHeight ? {} : {height: computedGridHeight}),
            }}
            rows={rows}
            disableRowSelectionOnClick
            columns={columns}
            getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? "even" : "odd")}
            onRowClick={handleRowClick}
            getRowId={(row) => row.__rowIndex}
            ////////////////////////////////////////////////////////////////////////////////
            // Auto-grow the editing row when a row banner is active so the row sizes to  //
            // its actual content (inputs + spacer) without stretching cells vertically.  //
            // The outer grid height is set explicitly via `sx.height` above — MUI does   //
            // not support `autoHeight` together with variable row heights, so we replace //
            // autoHeight with a computed height that accounts for the banner reservation. //
            ////////////////////////////////////////////////////////////////////////////////
            getRowHeight={(params) => (params.id === editingRowId && bannerActive ? "auto" : null)}
            ////////////////////////////////////////////////////////////////////////////////
            // MUI X v7 only re-renders rows with the "auto" height when the estimated    //
            // total page height CHANGES - if the estimate for a newly-auto row equals    //
            // the cached fixed height, the dimension update no-ops and the row's         //
            // ResizeObserver never attaches, so it can never grow.  A larger estimate    //
            // for the expanded row forces the re-render; the observer then corrects it.  //
            // (Same fix as RecordQuery.tsx.)                                             //
            ////////////////////////////////////////////////////////////////////////////////
            getEstimatedRowHeight={(params) => (params.id === editingRowId && bannerActive ? 80 : null)}
            slots={{
               row: EditableRowComponent,
               toolbar: CustomToolbar
            }}
            // onPinnedColumnsChange={handlePinnedColumnsChange}
            // pagination
            // paginationMode="server"
            // rowsPerPageOptions={[20]}
            // sortingMode="server"
            // filterMode="server"
            // page={pageNumber}
            // checkboxSelection
            rowCount={data?.totalRows != null ? data.totalRows - deletedRowCount : data?.totalRows}
            // onPageSizeChange={handleRowsPerPageChange}
            // onStateChange={handleStateChange}
            density={gridDensity ?? "standard"}
            disableVirtualization={editingRowId != null}
            ////////////////////////////////////////////////////////////////////////////////////
            // mui-x v7 virtualizes rows+columns by pixels (default ~150px) where v5 buffered //
            // whole rows/columns (rowBuffer={10}) - keep off-viewport cells in the DOM so     //
            // selenium suites and downstream consumers can assert cells behind the scroll     //
            ////////////////////////////////////////////////////////////////////////////////////
            columnBufferPx={10000}
            rowBufferPx={10000}
            // loading={loading}
            // filterModel={filterModel}
            // onFilterModelChange={handleFilterChange}
            // columnVisibilityModel={columnVisibilityModel}
            // onColumnVisibilityModelChange={handleColumnVisibilityChange}
            // onColumnOrderChange={handleColumnOrderChange}
            // onSelectionModelChange={selectionChanged}
            // onSortModelChange={handleSortChange}
            // sortingOrder={[ "asc", "desc" ]}
            // sortModel={columnSortModel}
         />
         <HoverRowActionsOverlay
            apiRef={gridApiRef}
            isRowEditable={associationMode ? Boolean(allowRecordEdit && editRecordCallback) : isRowEditable}
            onEnterEdit={associationMode ? (rowId) => editRecordCallback(Number(rowId)) : rowEditOnEnterEdit}
            rowEditDisabledMessage={associationMode ? undefined : rowEditDisabledMessage}
            isRowDeletable={associationMode ? Boolean(allowRecordDelete && deleteRecordCallback) : isRowDeletable}
            onDeleteClick={associationMode ? (rowId) => deleteRecordCallback(Number(rowId)) : (rowId) => setDeleteConfirmRowId(rowId)}
            deleteDisabledReason={associationMode ? undefined : rowDeleteDisabledMessage}
         />
         <SavedRowOverlay apiRef={gridApiRef} onEnterEdit={rowEditOnEnterEdit} />
         <EditingRowBannerOverlay apiRef={gridApiRef} />
         <Dialog open={deleteConfirmRowId != null} onClose={() => !isDeleteSubmitting && closeDeleteDialog()}>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogContent>
               <DialogContentText>Are you sure you want to delete this record?</DialogContentText>
               {deleteError && <DialogContentText color="error">{deleteError}</DialogContentText>}
            </DialogContent>
            <DialogActions>
               <Button onClick={closeDeleteDialog} disabled={isDeleteSubmitting}>No</Button>
               <Button onClick={handleDeleteConfirm} autoFocus disabled={isDeleteSubmitting}>Yes</Button>
            </DialogActions>
         </Dialog>
      </RowEditContext.Provider>
   );

   if (gridOnly)
   {
      return (grid);
   }

   ///////////////////////////////////////////////////////////////////////////////////////
   // the modals are SIBLINGS of the Widget, not its children: Widget only renders its  //
   // children when it is expanded (Widget.tsx, collapsibleStateIsOpen), while the      //
   // header's Bulk Add button renders either way.  As children, clicking Bulk Add on a //
   // collapsed collapsible widget would flip the state and show nothing.               //
   ///////////////////////////////////////////////////////////////////////////////////////
   return (
      <>
         <Widget
            widgetMetaData={widgetMetaData}
            widgetData={data}
            labelAdditionalElementsLeft={labelAdditionalElementsLeft}
            labelAdditionalComponentsRight={labelAdditionalComponentsRight}
            labelBoxAdditionalSx={{position: "relative", top: "-0.375rem"}}
         >
            <Box mx={containerPadding} mb={containerPadding}>
               <Box>
                  {grid}
               </Box>
            </Box>
         </Widget>
         {
            showRecordPicker &&
            <RecordPickerModal
               tableName={data.bulkAddViaFilterPickTableName}
               initialQueryFilterJson={data.bulkAddViaFilterPickerFilter}
               lockedCriteriaCount={bulkAddPickerLockedCriteriaCount}
               heading={bulkAddPickTableMetaData ? `Bulk Add: ${bulkAddPickTableMetaData.label}` : "Bulk Add"}
               resolveToRecordIdsLimit={bulkAddAssociationMode ? BULK_ADD_ASSOCIATION_MAX_ROWS + 1 : undefined}
               onTableMetaDataLoaded={(tableMetaData) => setBulkAddPickTableMetaData(tableMetaData)}
               onSelect={(selection) => bulkAddAssociationMode ? addPickedRecordsAsAssociations(selection as string[]) : runBulkAddProcessForPickedRecords(selection)}
               onClose={() => setShowRecordPicker(false)}
            />
         }
         {
            bulkAddProcess &&
            <Modal open={true} onClose={(event, reason) => closeBulkAddProcess(event, reason)}>
               <div className="modalProcess">
                  <ProcessRun process={bulkAddProcess} isModal={true} table={bulkAddPickTableMetaData} recordIds={bulkAddSelection} defaultProcessValues={{parentRecordId: bulkAddParentRecordId}} closeModalHandler={closeBulkAddProcess} />
               </div>
            </Modal>
         }
      </>
   );
}

export default RecordGridWidget;
